import { memo, useMemo } from 'react';

import type { Deposit } from '@didaskill/domain/assignment-submissions/types';
import type { Nullable } from '@didaskill/types/misc';
import { Stepper } from '@didaskill/ui/design-system';

import { AssignmentStep } from './assignment-step/AssignmentStep';
import { ASSIGNMENT_STEPS } from './constants';

type AssignmentStepperProps = {
  deposit: Nullable<Deposit>;
};

const getStepForIndex = (hasOral: boolean, index: 0 | 1) => {
  if (hasOral) {
    return index === 0 ? ASSIGNMENT_STEPS.APPOINTMENT_SCHEDULED : ASSIGNMENT_STEPS.ORAL_DONE;
  }

  return index === 0 ? ASSIGNMENT_STEPS.SUBMITTED : ASSIGNMENT_STEPS.CORRECTED;
};

export const AssignmentStepperBase = ({ deposit }: AssignmentStepperProps) => {
  const isDone = useMemo(() => !!deposit?.correction, [deposit]);

  const isOralDeposit = deposit && 'calendlyUrl' in deposit;

  const completedSteps: ASSIGNMENT_STEPS[] = useMemo(() => {
    if (!deposit) return [];

    if (isOralDeposit) {
      if (deposit.correction && 'oralExamPassedAt' in deposit.correction) {
        return [ASSIGNMENT_STEPS.APPOINTMENT_SCHEDULED, ASSIGNMENT_STEPS.ORAL_DONE];
      }

      if (deposit.calendlyUrl) {
        return [ASSIGNMENT_STEPS.APPOINTMENT_SCHEDULED];
      }

      return [];
    }

    if (deposit.correction) {
      return [ASSIGNMENT_STEPS.SUBMITTED, ASSIGNMENT_STEPS.CORRECTED];
    }

    if (deposit.submissionDate) {
      return [ASSIGNMENT_STEPS.SUBMITTED];
    }

    return [];
  }, [deposit, isOralDeposit]);

  return (
    <Stepper variant={Stepper.VARIANTS.HORIZONTAL}>
      <AssignmentStep
        isDone={isDone}
        isCurrent={completedSteps.includes(getStepForIndex(isOralDeposit, 0))}
        step={getStepForIndex(isOralDeposit, 0)}
      />

      <AssignmentStep
        isDone={isDone}
        isCurrent={completedSteps.includes(getStepForIndex(isOralDeposit, 1))}
        step={getStepForIndex(isOralDeposit, 1)}
      />
    </Stepper>
  );
};

AssignmentStepperBase.displayName = 'AssignmentStepper';

export const AssignmentStepper = memo(AssignmentStepperBase);
